import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "./App.css";
import AutoExpandingTextArea from "./Components/AutoExpandingTextArea.tsx";

interface PublicPlayerData {
  sessionId: string;
  name: string;
}

interface AIRoomNameEntryProps {
  roomName: string;
  sessionId: string;
  socket: any;
  setAIRoomState: (state: string) => void;
  setRoomState: (state: string) => void;
  players: PublicPlayerData[];
  showInfoMessage: (
    type: string,
    message: string,
    isAdd: boolean,
    duration?: string,
    isDelay?: boolean,
  ) => void;
}

const JOIN_ACK_TIMEOUT_MS = 3000;

function AIRoomNameEntry({
  roomName,
  sessionId,
  socket,
  setAIRoomState,
  setRoomState,
  players,
  showInfoMessage,
}: AIRoomNameEntryProps) {
  const { t } = useTranslation();
  const [nameInput, setNameInput] = useState("");
  const textAreaRef = useRef<HTMLTextAreaElement>(null);
  const submittingRef = useRef(false);

  // If this session already has a name (reconnection), skip to lobby. Skipped
  // while a submit is in flight: the server broadcasts the new player list
  // before the join ack, and the ack decides between settings and lobby.
  useEffect(() => {
    if (submittingRef.current) return;
    const existingPlayer = players.find((p) => p.sessionId === sessionId);
    if (existingPlayer && existingPlayer.name) setAIRoomState("lobby");
  }, [players, sessionId, setAIRoomState]);

  const submitName = () => {
    const name = nameInput.trim();
    if (!name) {
      showInfoMessage("warning", t("ai.nameEntry.nameRequired"), false);
      return;
    }
    if (submittingRef.current) return;
    submittingRef.current = true;

    let settled = false;
    const finish = (next: string) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      submittingRef.current = false;
      setAIRoomState(next);
    };
    // If the ack never arrives, fall through to the lobby anyway.
    const timer = setTimeout(() => finish("lobby"), JOIN_ACK_TIMEOUT_MS);

    socket.emit(
      "ai room join",
      { roomName, sessionId, name },
      (res: { ok?: boolean; isOwner?: boolean } | undefined) => {
        finish(res && res.isOwner ? "settings" : "lobby");
      },
    );
  };

  return (
    <div className="App-header">
      <div className="container-row lift-topbar">
        <div className="container-row-start">
          <div
            className="button-tertiary-m button-text-m text-color-black"
            onClick={() => setRoomState("start screen")}
            style={{ maxHeight: "32.5px" }}
          >
            {t("back")}
          </div>
        </div>
        <div className="text-h3 text-color-black left-margin-s text-force-break">
          {roomName}
        </div>
      </div>

      <div
        className="App-header"
        style={{ justifyContent: "flex-end", gap: 0 }}
      >
        <div style={{ marginTop: 32 }}>
          <p className="text-h2 text-color-black" style={{ marginBottom: 24 }}>
            {t("ai.nameEntry.title")}
          </p>
        </div>

        <div style={{ flex: "1 1 auto" }} />

        <div className="container">
          <AutoExpandingTextArea
            value={nameInput}
            onChange={setNameInput}
            textareaRef={textAreaRef}
            placeholder={t("ai.nameEntry.placeholder")}
            maxLength={20}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                submitName();
              }
            }}
          />
        </div>
      </div>

      <div
        className="button-primary black-fill button-text-l text-color-pink shadow-pink box-button-center"
        onClick={submitName}
      >
        {t("next")}
      </div>
    </div>
  );
}

export default AIRoomNameEntry;
